import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Check, ShieldCheck, UserPlus, LogIn, Calendar, Phone, MailCheck,
  Eye, Ear, Stethoscope, Users, Building2, Network,
} from "lucide-react";
import SEO, { breadcrumb } from "../components/SEO";
import MssanteDomainCheck from "../components/MssanteDomainCheck";
import MssanteIndividualVsStructure from "../components/MssanteIndividualVsStructure";
import { Section, FAQList, BrandImageBlock, OhMyVueIntegrationBlock, FinalCTABlock } from "./mssante/_sections";

/**
 * MSSantéPersonaPage — reusable shell for the SEO sister landing pages.
 * Keeps logic minimal so each persona only ships its own copy/keywords.
 */
export function MssantePersonaPage({
  // SEO
  title, description, canonical, h1, intro,
  // Identity
  eyebrow, eyebrowIcon: EyebrowIcon = ShieldCheck,
  // Persona-specific
  ctaPrimaryLabel = "Créer ma boîte MSSanté",
  audienceLabel,                  // e.g., "Opticien", "Audioprothésiste"
  exchangePartners = [],          // [{icon, label, desc}]
  highlights = [],                // 4 short bullet points
  ecosystem,                      // {title, description, links: [{label, href, target?}]}
  faqs = [],                      // schema FAQ
  serviceName,                    // schema Service.name
  serviceAudience,                // schema Service.audience.audienceType
  // Cross-links
  siblings = [],                  // [{to, label, icon, desc}]
}) {
  const schema = [
    breadcrumb([
      { name: "Accueil", url: "/" },
      { name: "MSSanté", url: "/mssante" },
      { name: audienceLabel || h1, url: canonical },
    ]),
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: serviceName,
      serviceType: "MSSanté",
      provider: { "@type": "Organization", name: "MetaConnect", url: "https://metaconnect.fr" },
      areaServed: { "@type": "Country", name: "France" },
      audience: { "@type": "BusinessAudience", audienceType: serviceAudience },
      description,
      offers: {
        "@type": "Offer",
        priceCurrency: "EUR",
        availability: "https://schema.org/InStock",
        url: "https://metaconnect.fr/mssante/inscription",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  return (
    <div className="bg-[var(--mc-navy-950)]" data-testid={`page-mssante-persona-${audienceLabel?.toLowerCase().replace(/\s+/g, "-")}`}>
      <SEO title={title} description={description} canonical={canonical} schema={schema} />

      {/* === Hero === */}
      <section className="hex-grid pt-32 pb-16 lg:pt-40 lg:pb-20" aria-labelledby="hero-h1">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-emerald-600 mb-5">
            <EyebrowIcon size={13} /> {eyebrow}
          </div>
          <h1 id="hero-h1" className="font-display text-[#0F2B5B] text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05]">
            {h1}
          </h1>
          <p className="mt-5 text-[16px] text-[#475569] max-w-2xl mx-auto leading-relaxed">
            {intro}
          </p>
          <div className="mt-9 flex items-center justify-center gap-3 flex-wrap">
            <Link to="/mssante/inscription" data-testid="persona-cta-register"
                  className="btn-primary px-7 py-3.5 rounded-full font-medium inline-flex items-center gap-2">
              <UserPlus size={15} /> {ctaPrimaryLabel}
            </Link>
            <Link to="/demo" data-testid="persona-cta-demo"
                  className="btn-ghost px-7 py-3.5 rounded-full font-medium inline-flex items-center gap-2">
              <Calendar size={15} /> Demander une démo
            </Link>
            <Link to="/mssante/connexion" data-testid="persona-cta-login"
                  className="btn-ghost px-7 py-3.5 rounded-full font-medium inline-flex items-center gap-2">
              <LogIn size={15} /> J'ai déjà un compte
            </Link>
          </div>
          <p className="mt-5 text-[12px] text-[#475569]">
            Sans configuration technique · Mise en service 24-48 h · Sans engagement
          </p>
        </div>
      </section>

      {/* === Domain availability widget — moved up for visibility & lead-gen === */}
      <MssanteDomainCheck />

      {/* === Highlights === */}
      {highlights.length > 0 && (
        <Section variant="soft" eyebrow={{ icon: ShieldCheck, label: "Pourquoi MetaConnect ?", tone: "mint" }}
                 title={`MetaConnect pour ${audienceLabel?.toLowerCase()} — ce qui change`}>
          <div className="grid sm:grid-cols-2 gap-3 max-w-3xl mx-auto">
            {highlights.map((h) => (
              <div key={h} className="flex items-start gap-3 rounded-lg border border-[rgba(110,231,197,0.18)] bg-[rgba(110,231,197,0.04)] p-4">
                <div className="grid place-items-center h-7 w-7 rounded-full bg-[rgba(110,231,197,0.14)] text-emerald-600 shrink-0 mt-0.5">
                  <Check size={14} />
                </div>
                <p className="text-[13px] text-[#334155] leading-relaxed">{h}</p>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* === Exchange partners (parcours de soin) === */}
      {exchangePartners.length > 0 && (
        <Section variant="grid" eyebrow={{ icon: Network, label: "Parcours de soin", tone: "blue" }}
                 title="Avec qui vous échangez en MSSanté">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {exchangePartners.map((p) => (
              <div key={p.label} className="mc-card p-5">
                <div className="grid place-items-center h-10 w-10 rounded-lg bg-[rgba(45,123,255,0.18)] border border-[var(--mc-blue-400)] text-[#0F2B5B] mb-3">
                  <p.icon size={18} />
                </div>
                <h3 className="font-display text-[#0F2B5B] text-[15px]">{p.label}</h3>
                <p className="mt-1.5 text-[12.5px] text-[#475569] leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* === Brand image (personal vs organizational) === */}
      <BrandImageBlock />

      {/* === Individual (Mailiz) vs Structure (MetaConnect) === */}
      <MssanteIndividualVsStructure />

      {/* === Ecosystem (OhMyVue / OhMyOuie) === */}
      {ecosystem && <OhMyVueIntegrationBlock {...ecosystem} />}

      {/* === FAQ === */}
      {faqs.length > 0 && (
        <Section variant="soft" eyebrow={{ icon: ShieldCheck, label: "FAQ", tone: "blue" }}
                 title={`Questions fréquentes — MSSanté ${audienceLabel?.toLowerCase()}`}>
          <FAQList items={faqs} testidPrefix={`persona-${audienceLabel?.toLowerCase().replace(/\s+/g, "-")}-faq`} />
        </Section>
      )}

      {/* === Sister pages cross-link === */}
      {siblings.length > 0 && (
        <Section variant="grid" eyebrow={{ icon: Network, label: "Voir aussi", tone: "blue" }}
                 title="Pages dédiées par profession">
          <div className="grid md:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {siblings.map((s) => (
              <Link key={s.to} to={s.to}
                    data-testid={`persona-sibling-${s.to.split("/").pop()}`}
                    className="mc-card p-5 hover:border-[var(--mc-blue-400)] transition group">
                <div className="grid place-items-center h-10 w-10 rounded-lg bg-[rgba(45,123,255,0.18)] border border-[var(--mc-blue-400)] text-[#0F2B5B] mb-3">
                  <s.icon size={18} />
                </div>
                <h3 className="font-display text-[#0F2B5B] text-[15px] inline-flex items-center gap-1.5">
                  {s.label}
                  <ArrowRight size={14} className="text-[#0369A1] group-hover:translate-x-0.5 transition" />
                </h3>
                <p className="mt-1.5 text-[12.5px] text-[#475569] leading-relaxed">{s.desc}</p>
              </Link>
            ))}
          </div>
        </Section>
      )}

      <FinalCTABlock />
    </div>
  );
}

// Lucide re-exports for convenience in persona pages
export { Eye, Ear, Stethoscope, Users, Building2, Network };
